import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import type { RootState } from "../../../store/store";

import { getMyStoresPaginatedApi, StorePublic } from "../../../views/store/storeApi";
import {
  SlotRegistrationStatus,
  StoreRegistrationItem,
  ListStoreRegistrationsQuery,
  UpdateRegistrationStatusDto,
  updateRegistrationStatusApi,
  listStoreRegistrationsApi,
} from "../../../views/scheduling/schedulingApi";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import { SUPERVISOR_HOME_PAGE } from "../../supervisor/routes";
import { registrationStatusLabel, registrationStatusTone } from "../../supervisor/registrationStatus";

// O CSS antigo continua importado: as classes dele são globais e outras telas usam.
import "./UpdatePresenceTablePage.css";
import "./PresencePage.css";

const STORES_FETCH_LIMIT = 50;

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = (ymd ?? "").split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
}

function formatTimeFromIso(iso: string): string {
  return (iso ?? "").slice(11, 16);
}

function normalizeRangeYmd(startYmd: string, endYmd: string) {
  if (startYmd <= endYmd) return { startDay: startYmd, endDay: endYmd };
  return { startDay: endYmd, endDay: startYmd };
}

/** Segunda a domingo da semana de hoje. */
function currentWeek() {
  const d = new Date();
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: toYmd(monday), end: toYmd(sunday) };
}

const PRESENCE_STATUS_OPTIONS: SlotRegistrationStatus[] = ["APROVADO", "AUSENTE", "CANCELADO", "PENDENTE"];

/**
 * Presença dos freelancers (redesign de 15/10/2026). Mesma regra de antes: a
 * presença muda ao salvar a linha. No celular, cartões agrupados por turno.
 */
const UpdatePresenceTablePage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(SUPERVISOR_HOME_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");

  const selectedStore = useMemo(() => stores.find((s) => s.id === selectedStoreId) ?? null, [stores, selectedStoreId]);

  const fetchAllStores = useCallback(async () => {
    if (!token) return;

    try {
      setLoadingStores(true);
      const all: StorePublic[] = [];
      let page = 1;

      while (true) {
        const data = await getMyStoresPaginatedApi(token, page, STORES_FETCH_LIMIT);
        const items = data?.items ?? [];
        const total = data?.total ?? 0;
        all.push(...items);
        if ((total > 0 && all.length >= total) || items.length === 0) break;
        page += 1;
        if (page > 200) break;
      }

      setStores(all);
      setSelectedStoreId((prev) => prev || all[0]?.id || "");
    } catch {
      toast.error("Erro ao buscar lojas.");
    } finally {
      setLoadingStores(false);
    }
  }, [token]);

  const todayYmd = useMemo(() => toYmd(new Date()), []);
  const yesterdayYmd = useMemo(() => toYmd(new Date(Date.now() - 24 * 60 * 60 * 1000)), []);
  const week = useMemo(() => currentWeek(), []);
  const [selectedStartDayYmd, setSelectedStartDayYmd] = useState<string>(todayYmd);
  const [selectedEndDayYmd, setSelectedEndDayYmd] = useState<string>(todayYmd);

  const selectedRange = useMemo(
    () => normalizeRangeYmd(selectedStartDayYmd, selectedEndDayYmd),
    [selectedStartDayYmd, selectedEndDayYmd],
  );

  const setRange = (start: string, end: string) => {
    setSelectedStartDayYmd(start);
    setSelectedEndDayYmd(end);
  };
  const isRange = (start: string, end: string) => selectedRange.startDay === start && selectedRange.endDay === end;

  const [loadingRegs, setLoadingRegs] = useState(false);
  const [registrations, setRegistrations] = useState<StoreRegistrationItem[]>([]);

  const fetchRegistrations = useCallback(
    async (storeId: string, startDayYmd: string, endDayYmd: string) => {
      if (!token) {
        toast.error("Sessão expirada. Faça login novamente.");
        navigate("/login");
        return;
      }

      try {
        setLoadingRegs(true);
        const range = normalizeRangeYmd(startDayYmd, endDayYmd);
        const query: ListStoreRegistrationsQuery = { startDay: range.startDay, endDay: range.endDay };
        const data = await listStoreRegistrationsApi(storeId, token, query);

        const sorted = [...(data ?? [])].sort((a, b) => {
          const byDay = String((a as any).day ?? "").localeCompare(String((b as any).day ?? ""));
          if (byDay !== 0) return byDay;
          const byTime = (a.slot?.startAt ?? "").localeCompare(b.slot?.startAt ?? "");
          if (byTime !== 0) return byTime;
          return (a.freelancer?.nomeCompleto ?? "").localeCompare(b.freelancer?.nomeCompleto ?? "");
        });

        setRegistrations(sorted);
      } catch {
        toast.error("Erro ao listar inscrições da loja.");
      } finally {
        setLoadingRegs(false);
      }
    },
    [token, navigate],
  );

  const [editedStatusById, setEditedStatusById] = useState<Record<string, SlotRegistrationStatus>>({});
  const [savingById, setSavingById] = useState<Record<string, boolean>>({});

  const currentOf = (r: StoreRegistrationItem): SlotRegistrationStatus =>
    ((r.status ?? "PENDENTE") as SlotRegistrationStatus) ?? "PENDENTE";
  const targetOf = (r: StoreRegistrationItem): SlotRegistrationStatus => editedStatusById[r.id] ?? currentOf(r);
  const isDirty = (r: StoreRegistrationItem) => String(currentOf(r)).toUpperCase() !== String(targetOf(r)).toUpperCase();

  const setTarget = (r: StoreRegistrationItem, next: SlotRegistrationStatus) => {
    setEditedStatusById((prev) => {
      const cp = { ...prev };
      if (String(next).toUpperCase() === String(currentOf(r)).toUpperCase()) delete cp[r.id];
      else cp[r.id] = next;
      return cp;
    });
  };

  const undo = (regId: string) =>
    setEditedStatusById((prev) => {
      const cp = { ...prev };
      delete cp[regId];
      return cp;
    });

  const handleConfirmRow = async (r: StoreRegistrationItem) => {
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      navigate("/login");
      return;
    }

    if (!isDirty(r)) {
      toast.info("Nenhuma alteração para salvar nesta linha.");
      undo(r.id);
      return;
    }

    const target = targetOf(r);
    try {
      setSavingById((p) => ({ ...p, [r.id]: true }));
      const dto: UpdateRegistrationStatusDto = { status: target };
      await updateRegistrationStatusApi(r.id, token, dto);
      toast.success("Status atualizado com sucesso.");
      setRegistrations((prev) => prev.map((x) => (x.id === r.id ? ({ ...x, status: target } as any) : x)));
      undo(r.id);
    } catch {
      toast.error("Erro ao salvar alterações. Verifique permissões e dados.");
    } finally {
      setSavingById((p) => ({ ...p, [r.id]: false }));
    }
  };

  useEffect(() => {
    fetchAllStores();
  }, [fetchAllStores]);

  useEffect(() => {
    if (!selectedStoreId) return;
    fetchRegistrations(selectedStoreId, selectedRange.startDay, selectedRange.endDay);
    setEditedStatusById({});
  }, [selectedStoreId, selectedRange, fetchRegistrations]);

  // Contagem por situação, para o resumo.
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of registrations) {
      const key = String(r.status ?? "PENDENTE").toUpperCase();
      map[key] = (map[key] ?? 0) + 1;
    }
    return map;
  }, [registrations]);

  // Grupos do celular: dia + turno.
  const groups = useMemo(() => {
    const out: Array<{ key: string; title: string; items: StoreRegistrationItem[] }> = [];
    for (const r of registrations) {
      const day = String((r as any).day ?? "").slice(0, 10);
      const t1 = formatTimeFromIso(r.slot?.startAt ?? "");
      const t2 = formatTimeFromIso(r.slot?.endAt ?? "");
      const key = `${day}|${t1}|${t2}`;
      let g = out.find((x) => x.key === key);
      if (!g) {
        const multiDay = selectedRange.startDay !== selectedRange.endDay;
        g = { key, title: `${multiDay && day ? `${formatBrDate(day).slice(0, 5)} · ` : ""}Turno ${t1} – ${t2}`, items: [] };
        out.push(g);
      }
      g.items.push(r);
    }
    return out;
  }, [registrations, selectedRange]);

  const periodLabel =
    selectedRange.startDay === selectedRange.endDay
      ? formatBrDate(selectedRange.startDay)
      : `${formatBrDate(selectedRange.startDay)} a ${formatBrDate(selectedRange.endDay)}`;

  const statusPill = (status?: string | null) => (
    <span className={`presPill presPill--${registrationStatusTone(status)}`}>{registrationStatusLabel(status)}</span>
  );

  return (
    <div className="presPage">
      <main className="presPage__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate(SUPERVISOR_HOME_PAGE()) }, { label: "Presença dos freelancers" }]}
          title="Presença dos freelancers"
          subtitle="Confirme quem veio em cada turno. A mudança vale ao salvar a linha."
          onBack={goBack}
          backLabel="Voltar"
        />

        <section className="ecoCard presPage__filters" aria-label="Filtros">
          <div className="ecoField presPage__store">
            <label htmlFor="pres-loja">Loja</label>
            <select
              id="pres-loja"
              className="ecoSelect ecoSelect--sm"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              disabled={loadingStores}
            >
              {loadingStores ? (
                <option value="">Carregando lojas...</option>
              ) : stores.length === 0 ? (
                <option value="">Nenhuma loja encontrada</option>
              ) : (
                stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nomeFantasia}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="presPage__chips" role="group" aria-label="Atalhos de período">
            <button type="button" className="presChip" aria-pressed={isRange(todayYmd, todayYmd)} onClick={() => setRange(todayYmd, todayYmd)}>
              Hoje
            </button>
            <button
              type="button"
              className="presChip"
              aria-pressed={isRange(yesterdayYmd, yesterdayYmd)}
              onClick={() => setRange(yesterdayYmd, yesterdayYmd)}
            >
              Ontem
            </button>
            <button
              type="button"
              className="presChip"
              aria-pressed={isRange(week.start, week.end)}
              onClick={() => setRange(week.start, week.end)}
            >
              Esta semana
            </button>
          </div>

          <div className="ecoField presPage__date">
            <label htmlFor="pres-de">De</label>
            <input
              id="pres-de"
              type="date"
              className="ecoInput ecoInput--sm"
              value={selectedStartDayYmd}
              onChange={(e) => setSelectedStartDayYmd(e.target.value)}
              disabled={!selectedStoreId}
            />
          </div>
          <div className="ecoField presPage__date">
            <label htmlFor="pres-ate">Até</label>
            <input
              id="pres-ate"
              type="date"
              className="ecoInput ecoInput--sm"
              value={selectedEndDayYmd}
              onChange={(e) => setSelectedEndDayYmd(e.target.value)}
              disabled={!selectedStoreId}
            />
          </div>
        </section>

        <section className="ecoCard presPage__card" aria-label="Inscrições">
          <div className="presPage__head" aria-live="polite">
            <span className="presPage__count">
              {loadingRegs
                ? "Carregando inscrições..."
                : `${registrations.length} ${registrations.length === 1 ? "inscrição" : "inscrições"} · ${periodLabel}${
                    selectedStore ? ` · ${selectedStore.nomeFantasia}` : ""
                  }`}
            </span>
            {!loadingRegs && registrations.length ? (
              <span className="presPage__summary">
                {Object.entries(counts).map(([status, n]) => (
                  <span key={status} className={`presPill presPill--${registrationStatusTone(status)}`}>
                    {registrationStatusLabel(status)}: {n}
                  </span>
                ))}
              </span>
            ) : null}
          </div>

          {!selectedStoreId ? (
            <div className="presPage__emptyWrap">
              <p className="ecoEmpty">Selecione uma loja para visualizar as inscrições.</p>
            </div>
          ) : !loadingRegs && registrations.length === 0 ? (
            <div className="presPage__emptyWrap">
              <p className="ecoEmpty">Nenhuma inscrição encontrada para este período.</p>
            </div>
          ) : (
            <>
              {/* Computador: tabela */}
              <div className="ecoTableWrap presPage__table">
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th scope="col">Freelancer</th>
                      <th scope="col">Dia</th>
                      <th scope="col">Turno</th>
                      <th scope="col">Situação atual</th>
                      <th scope="col">Presença</th>
                      <th scope="col" className="ecoTable__num">
                        Ação
                      </th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loadingRegs}>
                    {registrations.map((r) => {
                      const regDay = String((r as any).day ?? "");
                      const dirty = isDirty(r);
                      const saving = !!savingById[r.id];
                      return (
                        <tr key={r.id} className={dirty ? "presPage__dirtyRow" : undefined}>
                          <td>
                            <span className="presPage__name">{r.freelancer?.nomeCompleto ?? "-"}</span>
                          </td>
                          <td className="ecoTable__muted">{regDay ? formatBrDate(regDay.slice(0, 10)) : "-"}</td>
                          <td className="ecoTable__muted presPage__nowrap">
                            {formatTimeFromIso(r.slot?.startAt ?? "")} – {formatTimeFromIso(r.slot?.endAt ?? "")}
                          </td>
                          <td>{statusPill(r.status)}</td>
                          <td>
                            <select
                              className={`ecoSelect ecoSelect--sm presPage__select${dirty ? " presPage__select--dirty" : ""}`}
                              aria-label={`Presença de ${r.freelancer?.nomeCompleto ?? "freelancer"}`}
                              value={targetOf(r)}
                              onChange={(e) => setTarget(r, e.target.value as SlotRegistrationStatus)}
                              disabled={saving}
                            >
                              {PRESENCE_STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                  {registrationStatusLabel(s)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="ecoTable__num">
                            {dirty ? (
                              <button
                                type="button"
                                className="ecoBtn ecoBtn--primary ecoBtn--sm"
                                onClick={() => handleConfirmRow(r)}
                                disabled={saving}
                              >
                                {saving ? "Salvando..." : "Salvar"}
                              </button>
                            ) : (
                              <span className="ecoTable__faint">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Celular: cartões por turno */}
              <div className="presPage__groups" aria-busy={loadingRegs}>
                {groups.map((g) => (
                  <section key={g.key} className="presPage__group" aria-label={g.title}>
                    <h2 className="presPage__groupTitle">
                      <span>{g.title}</span>
                      <span className="presPage__groupCount">
                        {g.items.length} {g.items.length === 1 ? "inscrito" : "inscritos"}
                      </span>
                    </h2>
                    {g.items.map((r) => {
                      const dirty = isDirty(r);
                      const saving = !!savingById[r.id];
                      const target = targetOf(r);
                      const name = r.freelancer?.nomeCompleto ?? "-";
                      return (
                        <article key={r.id} className={`presCard${dirty ? " presCard--dirty" : ""}`}>
                          <div className="presCard__top">
                            <span className="presCard__text">
                              <span className="presCard__name">{name}</span>
                              <span className="presCard__sub">
                                {dirty
                                  ? `Estava ${registrationStatusLabel(r.status).toLowerCase()} · alteração não salva`
                                  : r.freelancer?.cpf
                                    ? `CPF ${r.freelancer.cpf}`
                                    : ""}
                              </span>
                            </span>
                            {!dirty ? statusPill(r.status) : null}
                          </div>
                          <div className="presCard__options" role="radiogroup" aria-label={`Presença de ${name}`}>
                            {PRESENCE_STATUS_OPTIONS.map((s) => {
                              const checked = String(target).toUpperCase() === s;
                              return (
                                <button
                                  key={s}
                                  type="button"
                                  role="radio"
                                  aria-checked={checked}
                                  className={`presCard__option${checked ? ` presCard__option--${registrationStatusTone(s)}` : ""}`}
                                  onClick={() => setTarget(r, s)}
                                  disabled={saving}
                                >
                                  {registrationStatusLabel(s)}
                                </button>
                              );
                            })}
                          </div>
                          {dirty ? (
                            <div className="presCard__actions">
                              <button type="button" className="ecoBtn" onClick={() => undo(r.id)} disabled={saving}>
                                Desfazer
                              </button>
                              <button
                                type="button"
                                className="ecoBtn ecoBtn--primary"
                                onClick={() => handleConfirmRow(r)}
                                disabled={saving}
                              >
                                {saving ? "Salvando..." : "Salvar"}
                              </button>
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </section>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default UpdatePresenceTablePage;
